/**
 * PsicoSalud — Agenda de consultas (Módulo 1).
 *
 * Flujo crítico del sistema: es uno de los dos recorridos cubiertos por
 * las pruebas automatizadas de regresión (junto con el historial
 * clínico), por lo que sus controles llevan atributos `data-cy`.
 *
 * Al agendar una consulta REMOTA el backend crea automáticamente la
 * sala Jitsi y la vincula a la cita; desde aquí solo se navega a ella.
 */

import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { appointmentsApi, patientsApi } from '@/api';
import { ApiError } from '@/api/client';
import type { Appointment, Modality, PatientSummary } from '@/types';

/** Convierte un valor de <input type="datetime-local"> a ISO con zona. */
function toIso(localValue: string): string {
  return new Date(localValue).toISOString();
}

/** Devuelve el valor por defecto del formulario: mañana a las 09:00. */
function defaultStart(): string {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  date.setHours(9, 0, 0, 0);
  return toLocalInput(date);
}

function toLocalInput(date: Date): string {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

/** Suma la duración estándar de consulta (50 minutos) a un inicio dado. */
function addSessionDuration(localStart: string): string {
  const start = new Date(localStart);
  start.setMinutes(start.getMinutes() + 50);
  return toLocalInput(start);
}

export default function AgendaPage() {
  const { t } = useTranslation();

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [patients, setPatients] = useState<PatientSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showForm, setShowForm] = useState(false);

  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterModality, setFilterModality] = useState<'' | Modality>('');

  const [patientId, setPatientId] = useState('');
  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(() => addSessionDuration(defaultStart()));
  const [modality, setModality] = useState<Modality>('PRESENCIAL');
  // Cancelación: la cita elegida y el motivo. Sustituye al `window.confirm`,
  // que no admitía consignar por qué se cancela (tarea 6 del instrumento).
  const [cancelling, setCancelling] = useState<Appointment | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadAppointments = useCallback(async () => {
    setLoading(true);
    try {
      const list = await appointmentsApi.list({
        date_from: filterFrom ? toIso(filterFrom) : undefined,
        date_to: filterTo ? toIso(filterTo) : undefined,
        modality: filterModality || undefined,
      });
      setAppointments(list);
    } catch {
      setFeedback({ type: 'error', text: t('errors.network') });
    } finally {
      setLoading(false);
    }
  }, [filterFrom, filterTo, filterModality, t]);

  useEffect(() => {
    void loadAppointments();
  }, [loadAppointments]);

  useEffect(() => {
    void patientsApi
      .list({ limit: 200 })
      .then((page) => setPatients(page.pacientes))
      .catch(() => setPatients([]));
  }, []);

  // El fin de la consulta acompaña al inicio mientras el profesional no
  // lo ajuste manualmente hacia atrás.
  const handleStartChange = (value: string) => {
    setStart(value);
    if (!value) return;
    if (!end || new Date(end) <= new Date(value)) {
      setEnd(addSessionDuration(value));
    }
  };

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    setFeedback(null);
    setSubmitting(true);
    try {
      await appointmentsApi.create({
        patient_id: patientId,
        start_time: toIso(start),
        end_time: toIso(end),
        modality,
        notes: notes || undefined,
      });
      setFeedback({ type: 'success', text: t('agenda.created') });
      setShowForm(false);
      setNotes('');
      await loadAppointments();
    } catch (error) {
      const message =
        error instanceof ApiError && error.status === 409
          ? t('agenda.conflict')
          : error instanceof ApiError
            ? error.message
            : t('errors.generic');
      setFeedback({ type: 'error', text: message });
    } finally {
      setSubmitting(false);
    }
  };

  const confirmCancel = async () => {
    if (!cancelling) return;
    try {
      await appointmentsApi.cancel(cancelling.id, cancelReason);
      setFeedback({ type: 'success', text: t('agenda.cancelled') });
      setCancelling(null);
      setCancelReason('');
      await loadAppointments();
    } catch {
      setFeedback({ type: 'error', text: t('errors.generic') });
    }
  };

  return (
    <>
      <h1>{t('agenda.title')}</h1>

      {feedback && (
        <div
          className={`notice notice-${feedback.type}`}
          role="status"
          data-cy={`agenda-feedback-${feedback.type}`}
        >
          {feedback.text}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2>{t('common.search')}</h2>
          <button onClick={() => setShowForm((open) => !open)} data-cy="agenda-toggle-form">
            {showForm ? t('common.cancel') : t('agenda.newAppointment')}
          </button>
        </div>

        <div className="form-row">
          <div className="field">
            <label htmlFor="filter-from">{t('agenda.filterFrom')}</label>
            <input
              id="filter-from"
              type="datetime-local"
              value={filterFrom}
              onChange={(event) => setFilterFrom(event.target.value)}
              data-cy="agenda-filter-from"
            />
          </div>
          <div className="field">
            <label htmlFor="filter-to">{t('agenda.filterTo')}</label>
            <input
              id="filter-to"
              type="datetime-local"
              value={filterTo}
              onChange={(event) => setFilterTo(event.target.value)}
              data-cy="agenda-filter-to"
            />
          </div>
          <div className="field">
            <label htmlFor="filter-modality">{t('agenda.modality')}</label>
            <select
              id="filter-modality"
              value={filterModality}
              onChange={(event) => setFilterModality(event.target.value as '' | Modality)}
              data-cy="agenda-filter-modality"
            >
              <option value="">{t('agenda.allModalities')}</option>
              <option value="PRESENCIAL">{t('agenda.modalities.PRESENCIAL')}</option>
              <option value="REMOTA">{t('agenda.modalities.REMOTA')}</option>
            </select>
          </div>
        </div>
      </div>

      {showForm && (
        <div className="card">
          <h2>{t('agenda.newAppointment')}</h2>
          <form onSubmit={handleCreate} data-cy="agenda-form">
            <div className="form-row">
              <div className="field">
                <label htmlFor="patient">{t('agenda.patient')}</label>
                <select
                  id="patient"
                  required
                  value={patientId}
                  onChange={(event) => setPatientId(event.target.value)}
                  data-cy="agenda-patient"
                >
                  <option value="">—</option>
                  {patients.map((patient) => (
                    <option key={patient.id} value={patient.id}>
                      {patient.full_name} ({patient.document_id})
                    </option>
                  ))}
                </select>
              </div>

              <div className="field">
                <label htmlFor="modality">{t('agenda.modality')}</label>
                <select
                  id="modality"
                  value={modality}
                  onChange={(event) => setModality(event.target.value as Modality)}
                  data-cy="agenda-modality"
                >
                  <option value="PRESENCIAL">{t('agenda.modalities.PRESENCIAL')}</option>
                  <option value="REMOTA">{t('agenda.modalities.REMOTA')}</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="field">
                <label htmlFor="start">{t('agenda.start')}</label>
                <input
                  id="start"
                  type="datetime-local"
                  required
                  value={start}
                  onChange={(event) => handleStartChange(event.target.value)}
                  data-cy="agenda-start"
                />
              </div>
              <div className="field">
                <label htmlFor="end">{t('agenda.end')}</label>
                <input
                  id="end"
                  type="datetime-local"
                  required
                  value={end}
                  onChange={(event) => setEnd(event.target.value)}
                  data-cy="agenda-end"
                />
              </div>
            </div>

            <div className="field">
              <label htmlFor="notes">
                {t('agenda.notes')} <span className="hint">({t('common.optional')})</span>
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                data-cy="agenda-notes"
              />
            </div>

            <div className="button-row">
              <button type="submit" disabled={submitting || !patientId} data-cy="agenda-submit">
                {t('common.save')}
              </button>
              <button type="button" className="secondary" onClick={() => setShowForm(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <h2>{t('agenda.title')}</h2>

        {loading ? (
          <p className="empty-state">{t('common.loading')}</p>
        ) : appointments.length === 0 ? (
          <p className="empty-state" data-cy="agenda-empty">
            {t('agenda.empty')}
          </p>
        ) : (
          <div className="table-wrapper">
            <table data-cy="agenda-table">
              <thead>
                <tr>
                  <th>{t('agenda.start')}</th>
                  <th>{t('agenda.patient')}</th>
                  <th>{t('agenda.modality')}</th>
                  <th>{t('agenda.status')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((appointment) => (
                  <tr key={appointment.id} data-cy="agenda-row">
                    <td>{new Date(appointment.start_time).toLocaleString('es-PY')}</td>
                    <td data-cy="agenda-row-patient">{appointment.patient_name ?? '—'}</td>
                    <td>
                      <span className="badge badge-info">
                        {t(`agenda.modalities.${appointment.modality}`)}
                      </span>
                    </td>
                    <td data-cy="agenda-row-status">{t(`agenda.statuses.${appointment.status}`)}</td>
                    <td>
                      <div className="button-row">
                        {appointment.modality === 'REMOTA' && (
                          <Link to={`/consultas/${appointment.id}/sesion`} data-cy="agenda-join">
                            {t('agenda.join')}
                          </Link>
                        )}
                        <Link to={`/pacientes/${appointment.patient_id}/historial`}>
                          {t('agenda.openRecord')}
                        </Link>
                        {appointment.status !== 'CANCELLED' && (
                          <button
                            className="link"
                            onClick={() => {
                              setCancelling(appointment);
                              setCancelReason('');
                            }}
                            data-cy="agenda-cancel"
                          >
                            {t('agenda.cancelAppointment')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Diálogo de cancelación. Reemplaza al `window.confirm`, que no
          permitía consignar el motivo: dar de baja una consulta sin
          constancia del porqué pierde trazabilidad, y distinguir una
          cancelación del profesional de una inasistencia del paciente es
          clínicamente relevante para el seguimiento.

          El elemento `dialog` nativo se encarga del foco y del cierre con
          Escape; `open` basta porque el diálogo solo se monta cuando hay una
          cita seleccionada. */}
      {cancelling && (
        <dialog className="modal" open data-cy="agenda-cancel-dialog">
          <h2>{t('agenda.cancelAppointment')}</h2>
          <p className="t-suave t-pequeno">
            {new Date(cancelling.start_time).toLocaleString('es-PY')}
            {cancelling.patient_name ? ` · ${cancelling.patient_name}` : ''}
          </p>

          <div className="field">
            <label htmlFor="cancel-reason">{t('agenda.cancelReason')}</label>
            <textarea
              id="cancel-reason"
              rows={3}
              maxLength={500}
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
              placeholder={t('agenda.cancelReasonPlaceholder')}
              data-cy="agenda-cancel-reason"
            />
            <p className="t-suave t-pequeno" style={{ margin: '0.3rem 0 0' }}>
              {t('agenda.cancelReasonHint')}
            </p>
          </div>

          <div className="button-row">
            <button
              className="secondary"
              onClick={() => setCancelling(null)}
              data-cy="agenda-cancel-dismiss"
            >
              {t('common.back')}
            </button>
            <button
              className="danger"
              onClick={() => void confirmCancel()}
              data-cy="agenda-cancel-confirm"
            >
              {t('agenda.cancelAppointment')}
            </button>
          </div>
        </dialog>
      )}
    </>
  );
}
